import { SIX_PWA_EVENT_INSTALL_AVAILABLE } from '@/common/storage/events'

export type DeferredPrompt = Event & {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: string }>
}

function hasWindow() {
    return typeof window !== 'undefined'
}

export function isPwaInstalled() {
    if (!hasWindow()) return false
    try {
        const iosStandalone = (navigator as any).standalone === true
        const displayStandalone = window.matchMedia('(display-mode: standalone)').matches
        return iosStandalone || displayStandalone
    } catch {
        return false
    }
}

export function getDeferredInstallPrompt(): DeferredPrompt | null {
    if (!hasWindow()) return null
    return (window.__sixDeferredBeforeInstallPrompt as DeferredPrompt | undefined) ?? null
}

export function setDeferredInstallPrompt(prompt: Event) {
    if (!hasWindow()) return
    const deferred = prompt as DeferredPrompt
    deferred.preventDefault()
    window.__sixDeferredBeforeInstallPrompt = deferred
}

export function clearDeferredInstallPrompt() {
    if (!hasWindow()) return
    try {
        delete window.__sixDeferredBeforeInstallPrompt
    } catch {
        // ignore
    }
}

export async function promptDeferredInstall(): Promise<string | null> {
    const deferred = getDeferredInstallPrompt()
    if (!deferred) return null
    try {
        await deferred.prompt()
        const choice = await deferred.userChoice
        return choice.outcome
    } catch {
        return null
    } finally {
        clearDeferredInstallPrompt()
    }
}

export function subscribeInstallLifecycle(params: { onPromptAvailable: () => void; onInstalled: () => void }) {
    if (!hasWindow()) return () => {}

    let notifiedPrompt: DeferredPrompt | null = null

    const notifyPromptAvailableFor = (prompt?: DeferredPrompt | null) => {
        const current = prompt ?? getDeferredInstallPrompt()
        if (!current) return
        if (notifiedPrompt === current) return
        notifiedPrompt = current
        params.onPromptAvailable()
    }

    const onBeforeInstallPrompt = (event: Event) => {
        setDeferredInstallPrompt(event)
        notifyPromptAvailableFor(event as DeferredPrompt)
    }

    const onInstallAvailable = () => {
        const dp = getDeferredInstallPrompt()
        if (dp) notifyPromptAvailableFor(dp)
    }

    const onAppInstalled = () => {
        clearDeferredInstallPrompt()
        params.onInstalled()
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener(SIX_PWA_EVENT_INSTALL_AVAILABLE, onInstallAvailable)
    window.addEventListener('appinstalled', onAppInstalled)

    return () => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
        window.removeEventListener(SIX_PWA_EVENT_INSTALL_AVAILABLE, onInstallAvailable)
        window.removeEventListener('appinstalled', onAppInstalled)
    }
}
